'use client';

import { useState } from 'react';
import { dataLayerPush } from '@libs/analytics';
import { activateOffer } from '@libs/api-proxy-client';
import { logSentryError, SentryAcpTreatsRewardsErrors as SentryErrors, useAlertBanner, useLoyaltyContext, useOptimizely } from '@libs/common-next';
import type { Offer } from '@libs/common-types';
import { ensureAuth0AccessToken, evaluateSession, isUserLoggedIn } from '@libs/core';
import { SnackbarUtils } from '@libs/shared-ui/utils';
export function getCardSize(offer?: Offer, isFeatured?: boolean) {
  if (isFeatured) {
    return 'featured';
  }
  return offer?.cardSize.toLowerCase() === 'large' ? 'large' : 'small';
}
export function useOfferCardActions() {
  const {
    drawerOpen,
    getOffers,
    isActivating,
    selectedOffer,
    setDrawerOpen,
    setSelectedOffer
  } = useLoyaltyContext();
  const {
    setAlert
  } = useAlertBanner();
  const {
    context
  } = useOptimizely();
  const [activatingOffers, setActivatingOffers] = useState<Set<string>>(new Set());
  function handleCardClick(offer: Offer) {
    setDrawerOpen(true);
    setSelectedOffer(offer);
    context?.trackEvent('offer_details_modal_click');
    // Reset error so pre-existing error doesn't display inside offer drawer
    setAlert(undefined);
  }
  function handleCloseDrawer() {
    setDrawerOpen(false);
    setSelectedOffer(undefined);

    // Reset error so error from offer drawer doesn't display on offer grid
    setAlert(undefined);
  }
  async function handleActivateOffer(offer_0?: Offer, gtmSection?: string) {
    if (offer_0?.isActivatable && !offer_0.isActivated) {
      setActivatingOffers(prevOffers => new Set(prevOffers).add(offer_0.offerId));
      try {
        await evaluateSession({});
        await ensureAuth0AccessToken();
        if (!isUserLoggedIn()) {
          return;
        }
        const response = await activateOffer(offer_0);
        if (response && 'success' in response && response?.success) {
          setAlert(undefined);
          context?.trackEvent('offer_activations');

          // Update selectedOffer immediately to reflect activated state
          if (selectedOffer && selectedOffer.offerId === offer_0.offerId) {
            setSelectedOffer({
              ...selectedOffer,
              isActivated: true
            });
          }
          // Fetch offers again from API to get updated data after activation
          await getOffers();
        } else {
          throw new Error(`Failed to activate an offer, offerId: ${offer_0.offerId}`);
        }
      } catch (error) {
        SnackbarUtils.showPredefined('SERVER_ERROR');
        dataLayerPush({
          event: 'error',
          site_section: gtmSection || 'treats rewards',
          error_message: `uh oh! [${offer_0.shortDescription}] order didn't activate. please try again.`
        });
        logSentryError(error, SentryErrors.OfferCard.ActivateOfferFailed);
      } finally {
        setActivatingOffers(prevOffers => {
          const updatedSet = new Set(prevOffers);
          updatedSet.delete(offer_0.offerId);
          return updatedSet;
        });
      }
    }
  }
  function showSpinner(offer_1?: Offer) {
    return offer_1 ? activatingOffers.has(offer_1.offerId) : false;
  }
  return {
    drawerOpen,
    handleActivateOffer,
    handleCardClick,
    handleCloseDrawer,
    isActivating,
    selectedOffer,
    setDrawerOpen,
    setSelectedOffer,
    showSpinner
  };
}